import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  CardActions,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { collection, addDoc, updateDoc, doc, getDoc, arrayUnion, arrayRemove, Timestamp, setDoc, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

const Input = styled('input')({
  display: 'none',
});

const MAX_FILE_SIZE = 1024 * 1024; // 1MB

function GalleryManagement() {
  const theme = useTheme();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [loadingImages, setLoadingImages] = useState(false);
  const [eventImages, setEventImages] = useState([]);
  const [openNewEventDialog, setOpenNewEventDialog] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventDate, setNewEventDate] = useState('');

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) {
      setError('Event name is required');
      return;
    }

    try {
      const eventRef = collection(db, 'gallery_events');
      await addDoc(eventRef, {
        name: newEventName.trim(),
        date: newEventDate,
        createdAt: Timestamp.now(),
        images: []
      });

      setSuccess('Event created successfully!');
      setOpenNewEventDialog(false);
      setNewEventName('');
      setNewEventDate('');
      fetchEvents();
    } catch (error) {
      setError('Error creating event: ' + error.message);
    }
  };

  const fetchEvents = async () => {
    try {
      const eventsRef = collection(db, 'gallery_events');
      const querySnapshot = await getDocs(eventsRef);
      const eventsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
      setEvents(eventsList);
    } catch (error) {
      setError('Error fetching events: ' + error.message);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchEventImages();
    }
  }, [selectedEvent]);

  const fetchEventImages = async () => {
    if (!selectedEvent) return;
    
    setLoadingImages(true);
    try {
      const eventDoc = await getDoc(doc(db, 'gallery_events', selectedEvent));
      if (eventDoc.exists()) {
        setEventImages(eventDoc.data().images || []);
      }
    } catch (error) {
      setError('Error fetching images: ' + error.message);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleEventSelect = (eventId) => {
    setSelectedEvent(eventId);
    setPreviews([]);
    setSelectedFiles([]);
    setError('');
    setSuccess('');
  };

  const validateFiles = (files) => {
    const validFiles = [];
    const invalidFiles = [];

    Array.from(files).forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        invalidFiles.push(file.name);
      } else if (!file.type.startsWith('image/')) {
        invalidFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    return { validFiles, invalidFiles };
  };

  const handleFileSelect = (event) => {
    const { validFiles, invalidFiles } = validateFiles(event.target.files);

    if (invalidFiles.length > 0) {
      setError(`Some files were not added: ${invalidFiles.join(', ')} (Max size: 1MB, Images only)`);
    }

    setSelectedFiles(validFiles);
    setPreviews(validFiles.map(file => URL.createObjectURL(file)));
  };

  const handleUpload = async () => {
    if (!selectedEvent) {
      setError('Please select an event first');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const uploadPromises = selectedFiles.map(async (file) => {
        const storageRef = ref(storage, `gallery_events/${selectedEvent}/images/${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const url = await getDownloadURL(snapshot.ref);
        return {
          url,
          name: file.name,
          uploadedAt: Timestamp.now()
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);

      // Update Firestore
      const eventRef = doc(db, 'gallery_events', selectedEvent);
      await updateDoc(eventRef, {
        images: arrayUnion(...uploadedImages)
      });

      setSuccess('Images uploaded successfully!');
      setSelectedFiles([]);
      setPreviews([]);
      fetchEventImages();
    } catch (error) {
      setError('Error uploading images: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (image) => {
    setImageToDelete(image);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!imageToDelete || !selectedEvent) return;

    try {
      // Delete from Storage
      const storageRef = ref(storage, `gallery_events/${selectedEvent}/images/${imageToDelete.name}`);
      await deleteObject(storageRef);

      // Update Firestore
      const eventRef = doc(db, 'gallery_events', selectedEvent);
      await updateDoc(eventRef, {
        images: arrayRemove(imageToDelete)
      });

      setSuccess('Image deleted successfully!');
      fetchEventImages();
    } catch (error) {
      setError('Error deleting image: ' + error.message);
    } finally {
      setOpenDeleteDialog(false);
      setImageToDelete(null);
    }
  };

  const handleCreateEventClick = () => {
    setOpenNewEventDialog(true);
  };

  const handleImageSelect = () => {
    const fileInput = document.getElementById('image-upload');
    fileInput.click();
  };

  return (
    <Box 
      sx={{ 
        flexGrow: 1,
        pl: { xs: 0, sm: '240px' },
        pt: { xs: 1, sm: 2 },
        pb: { xs: 1, sm: 2 },
        width: '100%',
        height: 'calc(100vh - 64px)', // Subtract header height
        bgcolor: '#f8f9fa',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="xl" sx={{ height: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: '#580100',
            }}
          >
            Gallery Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: '1.2rem' }} />}
            onClick={handleCreateEventClick}
            sx={{
              bgcolor: '#580100',
              '&:hover': { bgcolor: '#3d0000' },
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            Create Event
          </Button>
        </Box>

        {/* Main Content */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          height: 'calc(100% - 48px)', // Subtract header height
          overflow: 'hidden'
        }}>
          {/* Events List */}
          <Paper 
            elevation={0}
            sx={{ 
              width: 300,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.03), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                Events
              </Typography>
            </Box>
            <Divider />
            <List sx={{ 
              p: 0.5, 
              overflow: 'auto',
              flexGrow: 1,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(theme.palette.primary.main, 0.1),
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: alpha(theme.palette.primary.main, 0.2),
              }
            }}>
              {events.map((event) => (
                <ListItem
                  key={event.id}
                  button
                  dense
                  selected={selectedEvent === event.id}
                  onClick={() => handleEventSelect(event.id)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    bgcolor: selectedEvent === event.id ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': {
                      bgcolor: selectedEvent === event.id 
                        ? alpha(theme.palette.primary.main, 0.15) 
                        : alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <EventIcon 
                      color={selectedEvent === event.id ? 'primary' : 'inherit'} 
                      sx={{ fontSize: '1.2rem' }}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography 
                        variant="body2"
                        color={selectedEvent === event.id ? 'primary' : 'textPrimary'}
                        sx={{ fontWeight: selectedEvent === event.id ? 600 : 400 }}
                      >
                        {event.name} ({event.images?.length || 0})
                      </Typography>
                    }
                    secondary={
                      event.date && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(event.date).toLocaleDateString()}
                        </Typography>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Images Section */}
          <Paper 
            elevation={0}
            sx={{ 
              flexGrow: 1,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ 
              p: 1.5, 
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                {selectedEvent 
                  ? `${events.find(e => e.id === selectedEvent)?.name} - Images` 
                  : 'Select an event to view images'}
              </Typography>
              {selectedEvent && (
                <Button
                  size="small"
                  startIcon={<CloudUploadIcon sx={{ fontSize: '1.2rem' }} />}
                  onClick={handleImageSelect}
                  sx={{
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    color: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      border: `1px solid ${theme.palette.primary.main}`
                    }
                  }}
                >
                  Upload
                </Button>
              )}
            </Box>
            <Divider />
            <Box sx={{ 
              p: 2, 
              flexGrow: 1, 
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(theme.palette.primary.main, 0.1),
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: alpha(theme.palette.primary.main, 0.2),
              }
            }}>
              {loadingImages ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : selectedEvent ? (
                <Box>
                  {/* Selected Images Preview */}
                  {previews.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 2 
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Selected Images ({previews.length})
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleUpload}
                          disabled={uploading}
                          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                          sx={{
                            bgcolor: '#580100',
                            '&:hover': { bgcolor: '#3d0000' },
                            textTransform: 'none'
                          }}
                        >
                          {uploading ? 'Uploading...' : 'Upload Images'}
                        </Button>
                      </Box>
                      <Grid container spacing={2}>
                        {previews.map((preview, index) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                              <CardMedia
                                component="img"
                                height="160"
                                image={preview}
                                alt={`Preview ${index + 1}`}
                                sx={{ objectFit: 'cover' }}
                              />
                              <CardActions sx={{ p: 0.5, justifyContent: 'flex-end' }}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    const newFiles = [...selectedFiles];
                                    const newPreviews = [...previews];
                                    newFiles.splice(index, 1);
                                    newPreviews.splice(index, 1);
                                    setSelectedFiles(newFiles);
                                    setPreviews(newPreviews);
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: '1.2rem' }} />
                                </IconButton>
                              </CardActions>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Existing Images */}
                  {eventImages.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                        Existing Images ({eventImages.length})
                      </Typography>
                      <Grid container spacing={2}>
                        {eventImages.map((image, index) => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                              <CardMedia
                                component="img"
                                height="160"
                                image={image.url}
                                alt={`Event image ${index + 1}`}
                                sx={{ objectFit: 'cover' }}
                              />
                              <CardActions sx={{ p: 0.5, justifyContent: 'flex-end' }}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(image)}
                                >
                                  <DeleteIcon sx={{ fontSize: '1.2rem' }} />
                                </IconButton>
                              </CardActions>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary'
                }}>
                  <EventIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
                  <Typography variant="body2">Select an event to view its images</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Hidden file input */}
        <Input
          accept="image/*"
          id="image-upload"
          multiple
          type="file"
          onChange={handleFileSelect}
        />

        {/* Create Event Dialog */}
        <Dialog open={openNewEventDialog} onClose={() => setOpenNewEventDialog(false)}>
          <DialogTitle>Create New Event</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Event Name"
              fullWidth
              value={newEventName}
              onChange={(e) => setNewEventName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              type="date"
              margin="dense"
              label="Event Date"
              fullWidth
              value={newEventDate}
              onChange={(e) => setNewEventDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNewEventDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateEvent} variant="contained">
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this image? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default GalleryManagement;
